import { motion, AnimatePresence } from 'framer-motion';
import { useOutletContext } from 'react-router-dom';
import { TrendingUp, Lock, ExternalLink, ChevronDown, ChevronUp, Brain } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { type CoachingSession, type SessionAnalysis } from '../../lib/coaching-sessions';

interface OutletCtx { isUnlocked: boolean; }
const WALKTHROUGH_URL = 'https://sessionintelligence.app/walkthrough';

const DEMO_PATTERNS = [
  {
    client: 'Alex',
    theme: 'Visibility and fear of judgement',
    sessions: 3,
    evidence: [
      { session: 'Session 1', quote: '"I don\'t want to put myself out there."' },
      { session: 'Session 2', quote: '"People will think I\'m being pushy."' },
      { session: 'Session 3', quote: '"I know I need to post, but I keep avoiding it."' },
    ],
    implication: 'This may suggest the client\'s business growth challenge is connected to visibility, identity, and emotional safety.',
    color: 'bg-blue-500',
    initials: 'AL',
  },
  {
    client: 'Sarah',
    theme: 'Guilt and people-pleasing',
    sessions: 3,
    evidence: [
      { session: 'Session 1', quote: '"I just don\'t want them to be upset with me."' },
      { session: 'Session 2', quote: '"I feel guilty even thinking about saying no."' },
      { session: 'Session 3', quote: '"I keep doing things I don\'t want to do."' },
    ],
    implication: 'The client may have a deeply held belief that their needs are less important than maintaining peace with others.',
    color: 'bg-success-40',
    initials: 'SA',
  },
  {
    client: 'David',
    theme: 'Avoidance of success',
    sessions: 3,
    evidence: [
      { session: 'Session 1', quote: '"Part of me wants to, but then I just... don\'t."' },
      { session: 'Session 2', quote: '"When I get close to finishing something, I lose motivation."' },
      { session: 'Session 3', quote: '"I\'ve started this three times."' },
    ],
    implication: 'There may be an unconscious conflict between the desire to succeed and a belief that success is unsafe or undeserved.',
    color: 'bg-orange-40',
    initials: 'DA',
  },
];

interface PatternItem {
  category: string;
  quote: string;
  label: string;
  sessionDate: string;
  sessionId: string;
  confidence: string;
}

interface ClientPatternGroup {
  clientId: string;
  clientName: string;
  initials: string;
  sessionCount: number;
  patterns: PatternItem[];
  topTheme: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Limiting Belief': 'bg-orange-50 text-orange-700',
  'Emotional Signal': 'bg-red-50 text-red-600',
  'Identity Statement': 'bg-success-10 text-success-50',
  'Parts Conflict': 'bg-teal-50 text-teal-700',
  'Meta Model Pattern': 'bg-blue-50 text-blue-700',
  'Values Clue': 'bg-brand-10 text-brand-60',
  'Avoidance Pattern': 'bg-amber-50 text-amber-700',
};

function extractPatternsFromAnalysis(analysis: SessionAnalysis, sessionDate: string, sessionId: string): PatternItem[] {
  const items: PatternItem[] = [];

  (analysis.limiting_beliefs || []).forEach(b => {
    if (b.client_quote) items.push({ category: 'Limiting Belief', quote: b.client_quote, label: b.possible_belief, sessionDate, sessionId, confidence: b.confidence });
  });
  (analysis.emotional_language_signals || []).forEach(e => {
    if (e.client_quote) items.push({ category: 'Emotional Signal', quote: e.client_quote, label: e.emotion_or_theme, sessionDate, sessionId, confidence: e.confidence });
  });
  (analysis.identity_statements || []).forEach(s => {
    if (s.client_quote) items.push({ category: 'Identity Statement', quote: s.client_quote, label: s.identity_frame, sessionDate, sessionId, confidence: s.confidence });
  });
  (analysis.parts_conflicts || []).forEach(p => {
    if (p.evidence_from_transcript) items.push({ category: 'Parts Conflict', quote: p.evidence_from_transcript, label: `${p.part_a} vs ${p.part_b}`, sessionDate, sessionId, confidence: p.confidence });
  });
  (analysis.meta_model_patterns || []).forEach(m => {
    if (m.client_quote) items.push({ category: 'Meta Model Pattern', quote: m.client_quote, label: m.pattern_type.replace(/_/g, ' '), sessionDate, sessionId, confidence: m.confidence });
  });
  (analysis.values_and_motivation_clues || []).forEach(v => {
    if (v.evidence) items.push({ category: 'Values Clue', quote: v.evidence, label: v.value_or_motivation, sessionDate, sessionId, confidence: v.confidence });
  });
  (analysis.avoidance_or_resistance_patterns || []).forEach(a => {
    if (a.evidence) items.push({ category: 'Avoidance Pattern', quote: a.evidence, label: a.pattern.replace(/_/g, ' '), sessionDate, sessionId, confidence: a.confidence });
  });

  return items;
}

function DemoLabel() {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-10 text-orange-70 text-xs font-semibold">Demo Preview</span>;
}

export function SIPatternHistoryPage() {
  const { isUnlocked } = useOutletContext<OutletCtx>();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [clientGroups, setClientGroups] = useState<ClientPatternGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!isUnlocked || !user) return;
    const load = async () => {
      setLoading(true);
      const { data: coach } = await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle();
      if (!coach) { setLoading(false); return; }

      const { data: sessions } = await supabase
        .from('coaching_sessions')
        .select('id, client_id, session_date, session_analysis')
        .eq('coach_id', coach.id)
        .eq('analysis_status', 'complete')
        .not('session_analysis', 'is', null)
        .order('session_date', { ascending: true });

      const { data: clients } = await supabase
        .from('coaching_clients')
        .select('id, full_name')
        .eq('coach_id', coach.id);

      if (!sessions || !clients) { setLoading(false); return; }

      const clientMap = Object.fromEntries(clients.map(c => [c.id, c.full_name]));
      const groupMap: Record<string, ClientPatternGroup> = {};
      const catCounts: Record<string, number> = {};

      for (const s of sessions as unknown as CoachingSession[]) {
        if (!s.session_analysis) continue;
        const clientId = s.client_id;
        const clientName = clientMap[clientId] || 'Unknown';
        const patterns = extractPatternsFromAnalysis(s.session_analysis, s.session_date, s.id);

        patterns.forEach(p => { catCounts[p.category] = (catCounts[p.category] || 0) + 1; });

        if (!groupMap[clientId]) {
          groupMap[clientId] = {
            clientId, clientName,
            initials: clientName.slice(0, 2).toUpperCase(),
            sessionCount: 0,
            patterns: [],
            topTheme: '',
          };
        }
        groupMap[clientId].sessionCount++;
        groupMap[clientId].patterns.push(...patterns);
      }

      // Determine top theme per client by most frequent category
      Object.values(groupMap).forEach(group => {
        const counts: Record<string, number> = {};
        group.patterns.forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
        const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
        group.topTheme = top ? `${top[0]} (${top[1]} signals)` : 'Multiple themes';
      });

      setCategoryCounts(catCounts);
      const groups = Object.values(groupMap).sort((a, b) => b.sessionCount - a.sessionCount);
      setClientGroups(groups);
      if (groups.length > 0) setExpanded(groups[0].clientId);
      setLoading(false);
    };
    load();
  }, [isUnlocked, user]);

  const hasRealData = clientGroups.length > 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-80">Pattern History</h1>
        <p className="text-sm text-gray-50 mt-1">
          Repeated patterns across multiple sessions — helping you see what keeps showing up for each client over time.
        </p>
      </motion.div>

      {!isUnlocked && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-2xl border border-gray-20 p-6 flex items-start gap-4"
        >
          <div className="p-3 bg-gray-10 rounded-xl flex-shrink-0">
            <Lock className="w-5 h-5 text-gray-40" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-80 mb-1">Pattern History Preview</h2>
            <p className="text-sm text-gray-50 mb-4 leading-relaxed">
              Once activated, Pattern History will show repeated limiting beliefs, emotional themes, identity statements, recurring Meta Model patterns, values conflicts, and language shifts across all your clients' sessions over time.
            </p>
            <a href={WALKTHROUGH_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-success-40 hover:bg-success-50 text-white rounded-xl text-sm font-semibold transition-colors">
              Unlock Pattern History on the Walkthrough
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </motion.div>
      )}

      {/* Pattern type overview */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-success-50" />
            <h2 className="text-sm font-semibold text-gray-80">Pattern Categories</h2>
          </div>
          {!hasRealData && <DemoLabel />}
        </div>
        <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hasRealData
            ? Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
                <div key={cat} className="bg-brand-5 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-gray-60">{cat}</p>
                    <span className="text-sm font-bold text-success-50">{count}</span>
                  </div>
                </div>
              ))
            : [
                { label: 'Limiting Beliefs', count: 6, desc: 'Fixed beliefs blocking progress' },
                { label: 'Emotional Signals', count: 9, desc: 'Emotional language patterns' },
                { label: 'Identity Statements', count: 4, desc: '"I am..." / "I\'m not..." statements' },
                { label: 'Parts Conflicts', count: 3, desc: 'Internal "part of me" conflicts' },
                { label: 'Meta Model Patterns', count: 11, desc: 'Deletions, distortions, generalisations' },
                { label: 'Values Clues', count: 5, desc: 'What matters most to the client' },
                { label: 'Avoidance Patterns', count: 4, desc: 'What the client moves away from' },
              ].map(p => (
                <div key={p.label} className="bg-brand-5 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-gray-60">{p.label}</p>
                    <span className="text-sm font-bold text-success-50">{p.count}</span>
                  </div>
                  <p className="text-xs text-gray-40">{p.desc}</p>
                </div>
              ))
          }
        </div>
      </motion.div>

      {/* Client pattern cards */}
      <div className="space-y-3">
        {isUnlocked && loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {isUnlocked && !loading && hasRealData && (
          <>
            <h2 className="text-sm font-semibold text-gray-60">Client Pattern History</h2>
            {clientGroups.map((group, i) => (
              <motion.div key={group.clientId}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-brand-5 transition-colors"
                  onClick={() => setExpanded(expanded === group.clientId ? null : group.clientId)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-success-40 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {group.initials}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-80">{group.clientName}</p>
                      <p className="text-xs text-gray-50">Top pattern: <span className="font-medium">{group.topTheme}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-40">{group.sessionCount} session{group.sessionCount !== 1 ? 's' : ''} · {group.patterns.length} signals</span>
                    {expanded === group.clientId ? <ChevronUp className="w-4 h-4 text-gray-40" /> : <ChevronDown className="w-4 h-4 text-gray-40" />}
                  </div>
                </button>
                <AnimatePresence>
                  {expanded === group.clientId && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                      className="overflow-hidden">
                      <div className="px-5 pb-5 border-t border-gray-10 pt-4 space-y-3">
                        {group.patterns.slice(0, 20).map((p, j) => (
                          <div key={j} className="flex items-start gap-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 mt-0.5 ${CATEGORY_COLORS[p.category] || 'bg-gray-10 text-gray-50'}`}>
                              {p.category}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-60 italic">"{p.quote}"</p>
                              <p className="text-xs text-gray-40 mt-0.5">
                                {p.label} · {new Date(p.sessionDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                        ))}
                        {group.patterns.length > 20 && (
                          <p className="text-xs text-gray-40 italic">And {group.patterns.length - 20} more signals across sessions.</p>
                        )}
                        <p className="text-xs text-gray-40 italic pt-1">These are coaching hypotheses. Validate with your client.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </>
        )}

        {isUnlocked && !loading && !hasRealData && (
          <div className="bg-white rounded-2xl border border-gray-20 p-8 text-center">
            <Brain className="w-8 h-8 text-gray-30 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-60">No session analyses yet</p>
            <p className="text-xs text-gray-40 mt-1">Upload and analyse your first session to see patterns appear here.</p>
          </div>
        )}

        {!isUnlocked && (
          <>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-60">Sample Pattern History</h2>
              <DemoLabel />
            </div>
            {DEMO_PATTERNS.map((pattern, i) => (
              <motion.div key={pattern.client}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05 }}
                className="bg-white rounded-2xl border border-gray-20 overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 hover:bg-brand-5 transition-colors"
                  onClick={() => setExpanded(expanded === pattern.client ? null : pattern.client)}>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full ${pattern.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                      {pattern.initials}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-80">{pattern.client}</p>
                      <p className="text-xs text-gray-50">Repeated pattern: <span className="font-medium">{pattern.theme}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-40">{pattern.sessions} sessions</span>
                    {expanded === pattern.client ? <ChevronUp className="w-4 h-4 text-gray-40" /> : <ChevronDown className="w-4 h-4 text-gray-40" />}
                  </div>
                </button>
                <AnimatePresence>
                  {expanded === pattern.client && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                      <div className="px-5 pb-5 border-t border-gray-10 pt-4 space-y-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-50 uppercase tracking-wide mb-2">Evidence Across Sessions</p>
                          <div className="space-y-2">
                            {pattern.evidence.map(e => (
                              <div key={e.session} className="flex items-start gap-3">
                                <span className="text-xs font-semibold text-gray-40 flex-shrink-0 mt-0.5 w-20">{e.session}</span>
                                <p className="text-sm text-gray-60 italic">{e.quote}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="bg-success-5 border border-success-10 rounded-xl p-3">
                          <p className="text-xs font-semibold text-success-50 mb-1">Possible Coaching Implication</p>
                          <p className="text-xs text-gray-60 leading-relaxed">{pattern.implication}</p>
                        </div>
                        <p className="text-xs text-gray-40 italic">These are coaching hypotheses. The coach should validate with the client.</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
